import type { PosCollection, PosSale, PosSession, PosZReport } from '@erp/shared';

import { api } from '../../lib/api.js';

import { sessionBreakdown, sessionDrawerCards, sessionSaleRows } from './shiftBreakdown.js';
import { buildShiftPrintHtml, printHtml } from './shiftReport.js';

type T = (key: string, opts?: Record<string, unknown>) => string;
type Money = (v: string | number, ccy: string) => string;

/**
 * Print a web-register shift's close-out (its Z-report): loads the Z-report,
 * the sales and the customers' debt payments, then prints the same slip the
 * shifts page does — sales by how they were paid, debt payments, each drawer.
 * Shared by the register's close (printed right after the count) and the
 * close-outs list, so the counter and the office print one document.
 */
export async function printSessionCloseout(
  session: PosSession,
  ctx: { t: T; money: Money; storeName: string; locale: string; rtl: boolean; cashier?: string | null; device?: string | null },
): Promise<void> {
  const [rates, z, sales, cols] = await Promise.all([
    api()
      .get<{ items: Array<{ code: string; rate: string }> }>('/currencies/display-rates', { params: { base: session.currencyCode } })
      .then((r) => Object.fromEntries(r.data.items.map((x) => [x.code, Number(x.rate)])) as Record<string, number>)
      .catch(() => ({} as Record<string, number>)),
    api().get<PosZReport>(`/pos/sessions/${session.id}/z-report`).then((r) => r.data).catch(() => null),
    api().get<{ items: PosSale[] }>('/pos/sales', { params: { sessionId: session.id, limit: 500 } }).then((r) => r.data.items).catch(() => [] as PosSale[]),
    api().get<{ items: PosCollection[] }>(`/pos/sessions/${session.id}/collections`).then((r) => r.data.items).catch(() => [] as PosCollection[]),
  ]);
  // The Z-report carries the session as the server has it now — closed, with
  // its counts — which is what the slip must say once the count is in.
  const s = z?.session ?? session;
  printHtml(buildShiftPrintHtml({
    number: s.sessionNumber,
    cashier: ctx.cashier ?? s.cashierName ?? null,
    device: ctx.device ?? null,
    openedAt: s.openedAt,
    closedAt: s.closedAt,
    salesCount: z?.byCurrency?.length ? z.byCurrency.reduce((acc, c) => acc + c.saleCount, 0) : (s.salesCount ?? 0),
    shiftCurrency: s.currencyCode,
    breakdown: sessionBreakdown(s, z, cols),
    drawers: sessionDrawerCards(s, z),
    note: s.closingNote,
    rows: sessionSaleRows(sales, cols, ctx.t, ctx.money),
  }, ctx.t, { storeName: ctx.storeName, locale: ctx.locale, rtl: ctx.rtl, rates }));
}
